import { RoundedBox } from '@react-three/drei'
import { paraArray } from '../../dominio/geometria/vetor3'
import type { AncoraOcupante, PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { ancoraPorId } from '../../dominio/didatico/perfilVeiculo'
import {
  RECLINACAO_ENCOSTO_RAD,
  RECUO_TRILHO,
  centrosDoBanco,
  idsDoBanco,
  larguraDoBanco,
  medidasDaAncora,
} from '../../dominio/didatico/bancos'
import { MaterialPeca, Peca } from '../componentes/Peca'
import { useMaquete } from '../componentes/contextoMaquete'
import { estadoDe } from '../../dominio/didatico/estadoPecas'
import { CaixaArredondada } from '../componentes/CaixaArredondada'

/** Interior simplificado: piso, painel, volante e um banco por âncora. */
export function Habitaculo({ perfil, deslocamentoPainel = 0, elevacaoPainel = 0 }: { perfil: PerfilVeiculo; deslocamentoPainel?: number; elevacaoPainel?: number }) {
  const motorista = ancoraPorId(perfil, 'motorista')

  return (
    <group>
      <Peca id="piso">
        {/* Assoalho principal da cabine */}
        <mesh position={[0, perfil.pisoY, (perfil.colunaA.zBase + perfil.colunaC.zBase) / 2]}>
          <boxGeometry args={[perfil.largura - 0.16, 0.04, perfil.colunaA.zBase - perfil.colunaC.zBase]} />
          <MaterialPeca id="piso" />
        </mesh>

        {/* Rampa frontal da pedaleira / corta-fogo inferior: fecha o vão dos pedais e apoia os pés */}
        <mesh
          position={[0, perfil.pisoY + 0.065, perfil.colunaA.zBase + 0.065]}
          rotation={[-0.42, 0, 0]}
        >
          <boxGeometry args={[perfil.largura - 0.18, 0.04, 0.22]} />
          <MaterialPeca id="piso" />
        </mesh>

        {/* Piso do porta-malas: fecha a área de carga até a traseira */}
        <mesh
          position={[
            0,
            perfil.pisoY,
            (perfil.colunaC.zBase + (perfil.traseiraZ + 0.14)) / 2,
          ]}
        >
          <boxGeometry
            args={[
              perfil.largura - 0.20,
              0.04,
              perfil.colunaC.zBase - (perfil.traseiraZ + 0.14),
            ]}
          />
          <MaterialPeca id="piso" />
        </mesh>

        {/* Túnel central longitudinal entre os bancos */}
        <mesh
          position={[
            0,
            perfil.pisoY + 0.045,
            (perfil.colunaA.zBase + 0.08 + perfil.colunaC.zBase) / 2,
          ]}
        >
          <boxGeometry
            args={[
              0.22,
              0.07,
              perfil.colunaA.zBase + 0.08 - perfil.colunaC.zBase,
            ]}
          />
          <MaterialPeca id="piso" />
        </mesh>
      </Peca>

      <Peca id="painel" deslocamentoAfastado={[0, 0.12, 0.35]}>
        <group position={[0, elevacaoPainel, deslocamentoPainel]}>
          <RoundedBox
            args={[perfil.largura - 0.14, 0.22, 0.34]}
            radius={0.05}
            smoothness={3}
            position={[0, perfil.cinturaY - 0.16, perfil.colunaA.zBase - 0.16]}
          >
            <MaterialPeca id="painel" />
          </RoundedBox>
        </group>
      </Peca>

      <group position={[0, elevacaoPainel, deslocamentoPainel]}>
        <Volante perfil={perfil} ancora={motorista} />
        <ConjuntoPedais perfil={perfil} ancora={motorista} />
      </group>
      <FreioDeMao perfil={perfil} ancora={motorista} />

      {/* um banco por posição de ocupante: encosto e apoio de cabeça são peças
          próprias porque atrapalham a retirada de formas diferentes */}
      {perfil.ancoras.map((ancora) => (
        <Banco key={ancora.id} perfil={perfil} ancora={ancora} />
      ))}
    </group>
  )
}

/**
 * Alavanca do freio de mão, entre os bancos dianteiros.
 *
 * `aberturaRad` reaproveita o mesmo mecanismo de porta/encosto: puxar o freio
 * é o procedimento interno que gira a peça — não uma animação própria.
 */
function FreioDeMao({ perfil, ancora }: { perfil: PerfilVeiculo; ancora: AncoraOcupante }) {
  const x = ancora.posicao.x - 0.24
  const y = ancora.alturaAssento - 0.06
  const z = ancora.posicao.z + 0.14
  return (
    <Peca id="freioDeMao" position={[x, y, z]} aberturaRad={-0.6} eixoAbertura="x">
      <mesh position={[0, 0, 0]}>
        <cylinderGeometry args={[0.014, 0.018, 0.06, 8]} />
        <MaterialPeca id="freioDeMao" metalico={0.4} rugosidade={0.5} />
      </mesh>
      <mesh position={[0, 0.09, 0]}>
        <cylinderGeometry args={[0.012, 0.012, 0.18, 8]} />
        <MaterialPeca id="freioDeMao" metalico={0.4} rugosidade={0.5} />
      </mesh>
      <mesh position={[0, 0.19, 0]}>
        <sphereGeometry args={[0.02, 10, 8]} />
        <MaterialPeca id="freioDeMao" metalico={0.2} rugosidade={0.6} />
      </mesh>
    </Peca>
  )
}

function Volante({ perfil, ancora }: { perfil: PerfilVeiculo; ancora: AncoraOcupante }) {
  // O motorista fica sempre no lado esquerdo do veículo (ancora.posicao.x > 0)
  const xMotorista = ancora.posicao.x
  const yHub = perfil.cinturaY - 0.10
  const zHub = perfil.colunaA.zBase - 0.36
  // Inclinação do volante automotivo: ~24° em relação à vertical, voltado para o peito do motorista
  const inclinacaoRad = (24 * Math.PI) / 180
  const rotacaoVolanteX = -(Math.PI - inclinacaoRad)
  const rotacaoColunaX = inclinacaoRad - Math.PI / 2
  const yColuna = perfil.cinturaY - 0.145
  const zColuna = perfil.colunaA.zBase - 0.265

  return (
    <Peca id="volante">
      {/* Conjunto do Volante (Aro, Cubo e Raios) inclinado em direção ao condutor */}
      <group position={[xMotorista, yHub, zHub]} rotation={[rotacaoVolanteX, 0, 0]}>
        {/* Aro circular ergonômico */}
        <mesh>
          <torusGeometry args={[0.17, 0.018, 14, 32]} />
          <MaterialPeca id="volante" rugosidade={0.45} />
        </mesh>
        {/* Cubo central (almofada da buzina / airbag frontal do motorista) */}
        <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0.006]}>
          <cylinderGeometry args={[0.065, 0.07, 0.035, 16]} />
          <MaterialPeca id="volante" rugosidade={0.55} />
        </mesh>
        {/* Emblema central */}
        <mesh position={[0, 0, 0.025]}>
          <circleGeometry args={[0.022, 16]} />
          <MaterialPeca id="volante" metalico={0.85} rugosidade={0.2} />
        </mesh>
        {/* Raio horizontal esquerdo */}
        <mesh position={[-0.09, 0, 0.004]}>
          <CaixaArredondada args={[0.12, 0.032, 0.016]} />
          <MaterialPeca id="volante" rugosidade={0.5} />
        </mesh>
        {/* Raio horizontal direito */}
        <mesh position={[0.09, 0, 0.004]}>
          <CaixaArredondada args={[0.12, 0.032, 0.016]} />
          <MaterialPeca id="volante" rugosidade={0.5} />
        </mesh>
        {/* Raio vertical inferior (design automotivo clássico de 3 raios) */}
        <mesh position={[0, -0.09, 0.004]}>
          <CaixaArredondada args={[0.032, 0.12, 0.016]} />
          <MaterialPeca id="volante" rugosidade={0.5} />
        </mesh>
      </group>

      {/* Coluna de direção saindo do painel e alinhada ao eixo do volante */}
      <mesh position={[xMotorista, yColuna, zColuna]} rotation={[rotacaoColunaX, 0, 0]}>
        <cylinderGeometry args={[0.034, 0.044, 0.22, 16]} />
        <MaterialPeca id="volante" rugosidade={0.65} />
      </mesh>

      {/* Alavancas de comando da coluna (setas e limpador de para-brisa) */}
      <mesh position={[xMotorista - 0.08, yHub - 0.015, zHub + 0.04]} rotation={[0, 0, 0.28]}>
        <cylinderGeometry args={[0.005, 0.005, 0.08, 8]} />
        <MaterialPeca id="volante" rugosidade={0.3} />
      </mesh>
      <mesh position={[xMotorista + 0.08, yHub - 0.015, zHub + 0.04]} rotation={[0, 0, -0.28]}>
        <cylinderGeometry args={[0.005, 0.005, 0.08, 8]} />
        <MaterialPeca id="volante" rugosidade={0.3} />
      </mesh>

      {/* Continuidade mecânica da coluna de direção atravessando a parede corta-fogo */}
      <Peca id="colunaDirecao" deslocamentoAfastado={[0, 0.08, 0.15]}>
        <mesh
          position={[xMotorista, yColuna - 0.12, zColuna + 0.18]}
          rotation={[rotacaoColunaX + 0.1, 0, 0]}
        >
          <cylinderGeometry args={[0.018, 0.02, 0.32, 12]} />
          <MaterialPeca id="colunaDirecao" metalico={0.8} rugosidade={0.3} />
        </mesh>
        {/* Cruzeta / junta colapsável */}
        <mesh position={[xMotorista, yColuna - 0.22, zColuna + 0.32]}>
          <CaixaArredondada args={[0.045, 0.045, 0.05]} />
          <MaterialPeca id="colunaDirecao" metalico={0.85} rugosidade={0.3} />
        </mesh>
      </Peca>
    </Peca>
  )
}

/** Conjunto de pedais na pedaleira do condutor (área crítica de aprisionamento de membros) */
function ConjuntoPedais({ perfil, ancora }: { perfil: PerfilVeiculo; ancora: AncoraOcupante }) {
  const xMotorista = ancora.posicao.x
  const yPedal = perfil.pisoY + 0.12
  const zPedal = perfil.colunaA.zBase + 0.04
  const espacamento = 0.07

  const pedaisConfig = [
    { nome: 'embreagem', offset: -espacamento, larguraSapata: 0.042, alturaSapata: 0.05 },
    { nome: 'freio', offset: 0, larguraSapata: 0.052, alturaSapata: 0.055 },
    { nome: 'acelerador', offset: espacamento, larguraSapata: 0.035, alturaSapata: 0.08 },
  ]

  return (
    <Peca id="pedais" deslocamentoAfastado={[0, 0.12, 0.15]}>
      {/* Barra de fixação dos pedais sob a pedaleira */}
      <mesh position={[xMotorista, yPedal + 0.16, zPedal]}>
        <CaixaArredondada args={[0.24, 0.025, 0.035]} />
        <MaterialPeca id="pedais" metalico={0.7} rugosidade={0.4} />
      </mesh>

      {pedaisConfig.map((p) => (
        <group key={p.nome} position={[xMotorista + p.offset, yPedal, zPedal]}>
          {/* Haste metálica descendo do suporte */}
          <mesh position={[0, 0.07, -0.02]} rotation={[0.3, 0, 0]}>
            <cylinderGeometry args={[0.007, 0.007, 0.16, 8]} />
            <MaterialPeca id="pedais" metalico={0.75} rugosidade={0.35} />
          </mesh>
          {/* Sapata de borracha antiderrapante */}
          <mesh position={[0, 0, -0.04]} rotation={[0.45, 0, 0]}>
            <CaixaArredondada args={[p.larguraSapata, p.alturaSapata, 0.015]} />
            <MaterialPeca id="pedais" corBase="#18181b" rugosidade={0.9} />
          </mesh>
        </group>
      ))}
    </Peca>
  )
}

/**
 * Banco de uma posição de ocupante.
 *
 * Medidas e centros vêm de `dominio/didatico/bancos.ts` — os mesmos que o
 * cálculo de obstáculos usa. O banco desenhado e o banco que atrapalha a rota
 * precisam ser o mesmo banco.
 */
function Banco({ perfil, ancora }: { perfil: PerfilVeiculo; ancora: AncoraOcupante }) {
  const { animacaoBancoExtracao, estados } = useMaquete()
  const m = medidasDaAncora(ancora)
  const ids = idsDoBanco(ancora)
  const centros = centrosDoBanco(ancora)
  const largura = larguraDoBanco(perfil, ancora)

  const animando = animacaoBancoExtracao?.idAncora === ancora.id || Boolean(animacaoBancoExtracao?.abaixarTodosBancos)
  const fracaoApoio = animando
    ? animacaoBancoExtracao!.fracaoApoioCabeca
    : estadoDe(estados, ids.apoioCabeca) === 'removida' ? 1 : 0
  const fracaoReclinar = animando
    ? animacaoBancoExtracao!.fracaoAbaixarBanco
    : estadoDe(estados, ids.encosto) === 'aberta' ? 1 : 0

  const anguloEncosto = m.encosto.inclinacaoRad + (animando
    ? -RECLINACAO_ENCOSTO_RAD * fracaoReclinar
    : 0)
  const aberturaRadEfetiva = animando ? 0 : -RECLINACAO_ENCOSTO_RAD

  // Deslocamento vertical do apoio de cabeça ao ser desencaixado
  const subidaApoio = Math.min(1, fracaoApoio * 1.4) * 0.16
  const apoioVisivel = fracaoApoio < 0.99 && estadoDe(estados, ids.apoioCabeca) !== 'removida'

  return (
    <Peca id={ids.conjunto} deslocamentoAfastado={[0, 0.14, 0]}>
      <Peca id={ids.assento} deslocamentoAfastado={[0, 0, -RECUO_TRILHO]}>
        <RoundedBox
          args={[largura, m.assento.altura, m.assento.profundidade]}
          radius={0.04}
          smoothness={3}
          position={paraArray(centros.assento)}
        >
          <MaterialPeca id={ids.assento} />
        </RoundedBox>
      </Peca>

      {/* o encosto reclina em torno da própria base, por isso o grupo é
          posicionado na dobradiça e a chapa é desenhada acima dela */}
      <Peca
        id={ids.encosto}
        position={[centros.encosto.x, ancora.alturaAssento, centros.encosto.z]}
        rotation={[anguloEncosto, 0, 0]}
        aberturaRad={aberturaRadEfetiva}
        eixoAbertura="x"
      >
        <RoundedBox
          args={[largura - 0.02, m.encosto.altura, m.encosto.espessura]}
          radius={0.04}
          smoothness={3}
          position={[0, m.encosto.offsetY, 0]}
        >
          <MaterialPeca id={ids.encosto} />
        </RoundedBox>

        {apoioVisivel && (
          <Peca id={ids.apoioCabeca}>
            <RoundedBox
              args={[m.apoioCabeca.largura, m.apoioCabeca.altura, m.apoioCabeca.espessura]}
              radius={0.04}
              smoothness={3}
              position={[0, m.apoioCabeca.offsetY + subidaApoio, m.apoioCabeca.offsetZ - m.encosto.offsetZ]}
            >
              <MaterialPeca id={ids.apoioCabeca} />
            </RoundedBox>
          </Peca>
        )}
      </Peca>
    </Peca>
  )
}
